import {defineStore} from 'pinia';
import axios from 'axios';
import type {ApiContinent, City, Continent, Country, County, StreetType} from "~/types/Address";

export const useAddressStore = defineStore('address', {
    state: () => ({
        continents: [] as Continent[],
        _fetchContinentsPromise: null as Promise<any> | null,
        countries: [] as Country[],
        _fetchCountriesPromise: null as Promise<any> | null,
        counties: [] as County[],
        _fetchCountiesPromise: null as Promise<any> | null,
        cities: [] as City[],
        _fetchCitiesPromise: null as Promise<any> | null,
        streetTypeList: [] as StreetType[],
        _fetchStreetTypeListPromise: null as Promise<any> | null,
    }),
    actions: {
        async fetchContinents() {
            if (this._fetchContinentsPromise) return this._fetchContinentsPromise;

            this._fetchContinentsPromise = (async () => {
                try {
                    const response = await axios.get(`${import.meta.env.VITE_API_BASE_URL}/api/v1/continents`);
                    if (response.data.data) {
                        this.continents = response.data.data.map((continent: ApiContinent) => ({
                            id: continent.id,
                            code: continent.code,
                            name: continent.value,
                        }));
                    }
                } catch (error) {
                    throw error;
                } finally {
                    this._fetchContinentsPromise = null;
                }
            })();

            return this._fetchContinentsPromise;
        },
        async getContinents(): Promise<Continent[]> {
            if (this.continents.length === 0) {
                await this.fetchContinents();
            }
            return this.continents;
        },
        async fetchCountriesByContinent(continentCode: string, excludedCountryCodes: string[] = []) {
            if (this._fetchCountriesPromise) return this._fetchCountriesPromise;

            this._fetchCountriesPromise = (async () => {
                try {
                    const response = await axios.get(`${import.meta.env.VITE_API_BASE_URL}/api/v1/continents/${continentCode}/countries?excluded_country_codes=${excludedCountryCodes.join(',')}`);
                    if (response.data.data) {
                        this.countries = response.data.data;
                    }
                } catch (error) {
                    throw error;
                } finally {
                    this._fetchCountriesPromise = null;
                }
            })();

            return this._fetchCountriesPromise;
        },
        async getCountriesByContinent(continentCode: string, excludedCountryCodes: string[] = []): Promise<Country[]> {
            await this.fetchCountriesByContinent(continentCode, excludedCountryCodes);
            return this.countries;
        },
        async fetchCountries() {
            if (this._fetchCountriesPromise) return this._fetchCountriesPromise;

            this._fetchCountriesPromise = (async () => {
                try {
                    const response = await axios.get(`${import.meta.env.VITE_API_BASE_URL}/api/v1/countries`);
                    if (response.data.data) {
                        this.countries = response.data.data;
                    }
                } catch (error) {
                    throw error;
                } finally {
                    this._fetchCountriesPromise = null;
                }
            })();

            return this._fetchCountriesPromise;
        },
        async getCountries(): Promise<Country[]> {
            if (this.countries.length === 0) {
                await this.fetchCountries();
            }
            return this.countries;
        },
        async fetchCounties() {
            if (this._fetchCountiesPromise) return this._fetchCountiesPromise;

            this._fetchCountiesPromise = (async () => {
                try {
                    const response = await axios.get(`${import.meta.env.VITE_API_BASE_URL}/api/v1/counties`);
                    if (response.data.data) {
                        this.counties = response.data.data.map((county: County) => ({
                            id: county.id,
                            code: county.code,
                            name: county.name,
                        }));
                    }
                } catch (error) {
                    throw error;
                } finally {
                    this._fetchCountiesPromise = null;
                }
            })();

            return this._fetchCountiesPromise;
        },
        async getCounties(): Promise<County[]> {
            if (this.counties.length === 0) {
                await this.fetchCounties();
            }
            return this.counties;

        },
        async fetchCities(countyId: number) {
            if (this._fetchCitiesPromise) return this._fetchCitiesPromise;

            this._fetchCitiesPromise = (async () => {
                try {
                    const response = await axios.get(`${import.meta.env.VITE_API_BASE_URL}/api/v1/counties/${countyId}/cities`);
                    if (response.data.data) {
                        this.cities = response.data.data.map((city: City) => ({
                            id: city.id,
                            name: city.name,
                            postal_code: city.postal_code,
                        }));
                    }
                } catch (error) {
                    throw error;
                } finally {
                    this._fetchCitiesPromise = null;
                }
            })();

            return this._fetchCitiesPromise;
        },
        async getCities(countyId: number): Promise<City[]> {
            await this.fetchCities(countyId);
            return this.cities;
        },
        async fetchStreetTypeList() {
            if (this._fetchStreetTypeListPromise) return this._fetchStreetTypeListPromise;

            this._fetchStreetTypeListPromise = (async () => {
                try {
                    const response = await axios.get(`${import.meta.env.VITE_API_BASE_URL}/api/v1/addresses/street-type-list`);
                    if (response.data.data) {
                        this.streetTypeList = response.data.data.map((streetType: StreetType) => ({
                            id: streetType.id,
                            name: streetType.name,
                        }));
                    }
                } catch (error) {
                    throw error;
                } finally {
                    this._fetchStreetTypeListPromise = null;
                }
            })();

            return this._fetchStreetTypeListPromise;
        },
        async getStreetTypeList(): Promise<StreetType[]> {
            if (this.streetTypeList.length === 0) {
                await this.fetchStreetTypeList();
            }
            return this.streetTypeList;
        },
    },
    persist: {
        storage: persistedState.sessionStorage,
        paths: [
            'counties',
            'continents',
            'streetTypeList',
        ],
        key: 'addressStore',
    }
});
